import * as signalR from "@microsoft/signalr";
import { API_BASE, withProfile } from "../api/base";

const NEGOTIATE_URL = `${API_BASE}/api/negotiate`;
const POST_URL = `${API_BASE}/api/guestUtter`;
const HOST_POST_URL = `${API_BASE}/api/hostUtter`;
const PRESENCE_POST_URL = `${API_BASE}/api/guestPresence`;
const HOST_STATUS_URL = `${API_BASE}/api/hostStatus`;

type GuestUtter = {
  sessionId: string;
  text: string;
  lang?: string;
  confidence?: number;
  ts?: number;
  id?: string;
  from?: "host" | "guest";
};

export class RoomHub {
  private hub?: signalR.HubConnection;
  private subsGuest = new Set<(u: GuestUtter) => void>();
  private subsHost = new Set<(u: GuestUtter) => void>();
  private subsPresence = new Set<
    (p: { sessionId: string; state: string; ts?: number }) => void
  >();
  private subsHostStatus = new Set<
    (s: {
      sessionId: string;
      status: "listening" | "paused" | "ended";
      ts?: number;
    }) => void
  >();
  private connected = false;
  private currentSessionId: string | null = null;
  private stopping = false;
  constructor(
    private negotiateUrl = NEGOTIATE_URL,
    private postUrl = POST_URL
  ) {}

  async connect(): Promise<void> {
    if (this.connected) return;
    const info = await fetch(withProfile(this.negotiateUrl)).then((r) =>
      r.json()
    );
    const logger: signalR.ILogger = {
      log: (level, message) => {
        if (level < signalR.LogLevel.Error) return;
        if (/WebSocket closed with status code: 1006/.test(String(message))) {
          return; // ignore expected close after session end
        }
        try {
          console.warn("[RoomHub]", message);
        } catch {}
      },
    };

    this.hub = new signalR.HubConnectionBuilder()
      .withUrl(info.url, { accessTokenFactory: () => info.accessToken })
      .configureLogging(logger)
      .withAutomaticReconnect()
      .build();

    this.hub.on("guest:utter", (payload: GuestUtter) => {
      this.subsGuest.forEach((fn) => fn(payload));
    });
    this.hub.on("host:utter", (payload: GuestUtter) => {
      this.subsHost.forEach((fn) => fn(payload));
    });
    this.hub.on(
      "guest:presence",
      (payload: { sessionId: string; state: string; ts?: number }) => {
        this.subsPresence.forEach((fn) => fn(payload));
      }
    );
    this.hub.on(
      "host:status",
      (payload: {
        sessionId: string;
        status: "listening" | "paused" | "ended";
        ts?: number;
      }) => {
        this.subsHostStatus.forEach((fn) => fn(payload));
      }
    );

    this.hub.onclose((err) => {
      if (this.stopping) return;
      if (!err) return;
      try {
        if (/1006/.test(String(err?.message || err))) return;
        console.warn("[RoomHub] connection closed:", err?.message || err);
      } catch {}
    });

    await this.hub.start();
    this.connected = true;
  }
  async join(sessionId: string): Promise<void> {
    this.currentSessionId = sessionId;
  }

  onGuestUtter(cb: (u: GuestUtter) => void) {
    this.subsGuest.add(cb);
    return () => this.subsGuest.delete(cb);
  }
  onHostUtter(cb: (u: GuestUtter) => void) {
    this.subsHost.add(cb);
    return () => this.subsHost.delete(cb);
  }
  onGuestPresence(
    cb: (p: { sessionId: string; state: string; ts?: number }) => void
  ) {
    this.subsPresence.add(cb);
    return () => this.subsPresence.delete(cb);
  }

  onHostStatus(
    cb: (s: {
      sessionId: string;
      status: "listening" | "paused" | "ended";
      ts?: number;
    }) => void
  ) {
    this.subsHostStatus.add(cb);
    return () => this.subsHostStatus.delete(cb);
  }

  async sendGuestUtter(u: GuestUtter) {
    // POST via the function; no hub.invoke needed
    const res = await fetch(withProfile(this.postUrl), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(u),
    });
    if (!res.ok) throw new Error(await res.text());
  }

  async sendHostUtter(u: GuestUtter) {
    // POST via the function for host utterances
    const res = await fetch(withProfile(HOST_POST_URL), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(u),
    });
    if (!res.ok) throw new Error(await res.text());
  }

  async sendGuestPresence(p: {
    sessionId: string;
    state: string;
    ts?: number;
  }) {
    const res = await fetch(withProfile(PRESENCE_POST_URL), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(p),
    });
    if (!res.ok) throw new Error(await res.text());
  }

  async disconnect() {
    try {
      this.stopping = true;
      await this.hub?.stop();
    } finally {
      this.hub = undefined;
      this.connected = false;
      this.currentSessionId = null;
      this.stopping = false;
    }
  }

  async sendHostStatus(p: {
    sessionId: string;
    status: "listening" | "paused" | "ended";
    ts?: number;
  }) {
    const res = await fetch(withProfile(HOST_STATUS_URL), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(p),
    });
    if (!res.ok) throw new Error(await res.text());
  }
}
